import type { AxiosInstance } from 'axios'
import type { IFactoryFunctionRequest, IFactoryResponse } from '@bc-library/interface'
import { EMethod } from '@bc-library/interface'
import useAxios from '@/services/api/useAxios'
import { API_MAIN_VERSION, BASE_URL } from '@/config/constantes'

const resource = 'trainingBudgets'

export type TrainingBudgetRow = {
  _id?: string
  trainingId: {
    _id: string
    display: string
    budget?: Array<{
      cost?: number
      quantity?: number
      linkToParticipants?: boolean
      status?: number
      costCategoryId?: { isInternalFunding?: boolean } | string | null
    }>
    categoryId?: Array<string | { _id: string }>
    itemCategories?: Array<{
      categoryId?: string | { _id: string }
      itemIds?: Array<string | { _id: string }>
    }>
  } | string
  months: number[]
  unitCost: number
  credit: number
  comment?: string
  checked?: boolean
}

export type TrainingBudgetDoc = {
  _id?: string
  __v?: number
  year: number
  target?: number
  trainings: TrainingBudgetRow[]
  countryId?: string
  officeId?: string
  _firmId?: string
  authorId?: string | { _id: string }
}

export type LeanBudgetSession = {
  _id: string
  trainingId: string
  budget: Array<{
    cost: number
    quantity: number
    linkToParticipants: boolean
    status: number
    costCategoryId: { isInternalFunding?: boolean } | null
  }>
  participants: Array<{
    isAttended: boolean
    hasCancelled: boolean
    cancellationCost: number
  }>
}

export type TrainingBudgetYearPayload = {
  budget: TrainingBudgetDoc | null
  sessions: LeanBudgetSession[]
}

export type YearRange = {
  min: number
  max: number
}

const getStartYear = (axios: IFactoryFunctionRequest<YearRange, undefined>) => (): IFactoryResponse<YearRange> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/start/year`,
  })
}

const getYearV2 = (axios: IFactoryFunctionRequest<TrainingBudgetYearPayload, undefined>) => (year: number): IFactoryResponse<TrainingBudgetYearPayload> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/year/${year}`,
  })
}

const create = (axios: IFactoryFunctionRequest<TrainingBudgetDoc, TrainingBudgetDoc>) => (data: TrainingBudgetDoc): IFactoryResponse<TrainingBudgetDoc> => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/`,
    data,
  })
}

const update = (axios: IFactoryFunctionRequest<TrainingBudgetDoc, TrainingBudgetDoc>) => (id: string, data: TrainingBudgetDoc): IFactoryResponse<TrainingBudgetDoc> => {
  return axios({
    method: EMethod.put,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/${id}`,
    data,
  })
}

export default (axios: AxiosInstance) => ({
  getStartYear: getStartYear(useAxios(axios)),
  getYearV2: getYearV2(useAxios(axios)),
  create: create(useAxios(axios)),
  update: update(useAxios(axios)),
})
